import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { LoadingSkeleton } from '../ui/LoadingStates';
import { optimizeImageUrl } from '../../utils/imageUtils';
import { debugLog, debugWarn } from '../../utils/debugLog';
import { getCachedImage, isImageCacheAvailable, releaseObjectUrl } from '../../services/imageCacheService';
import type { CSSProperties } from 'react';
import { DEFAULT_SHOW_FALLBACK_IMAGE } from '../../constants/images';

interface ImageCandidate {
  src: string;
  width?: number;
  height?: number;
}

interface OptimizedContentImageProps {
  src?: string | null;
  alt: string;
  className?: string;
  imageClassName?: string;
  imageStyle?: CSSProperties;
  fit?: 'cover' | 'contain';
  priority?: boolean;
  preload?: boolean;
  preconnect?: boolean;
  sizes?: string;
  candidates?: ImageCandidate[];
  fallbackSrc?: string;
  onLoadComplete?: (loadTime: number) => void;
}

const DEFAULT_FALLBACK = DEFAULT_SHOW_FALLBACK_IMAGE;

const DIMENSION_REGEX = /(?<width>\d+)x(?<height>\d+)/;

const normalizeCandidate = (candidate: ImageCandidate): ImageCandidate => {
  const optimized = optimizeImageUrl(candidate.src);

  const match = optimized.match(DIMENSION_REGEX);
  if (match?.groups && !candidate.width && !candidate.height) {
    return {
      src: optimized,
      width: Number(match.groups.width),
      height: Number(match.groups.height)
    };
  }

  return {
    src: optimized,
    width: candidate.width,
    height: candidate.height
  };
};

const OptimizedContentImage: React.FC<OptimizedContentImageProps> = ({
  src,
  alt,
  className = '',
  imageClassName = '',
  imageStyle,
  fit = 'cover',
  priority = false,
  preload = false,
  preconnect = false,
  sizes = '100vw',
  candidates,
  fallbackSrc = DEFAULT_FALLBACK,
  onLoadComplete
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [inView, setInView] = useState(process.env.NODE_ENV === 'test' || priority);
  const [isBlobSource, setIsBlobSource] = useState(false);

  const loadStartRef = useRef(Date.now());
  const cacheHitRef = useRef(false);
  const objectUrlRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const resolvedSrcRef = useRef<string>('');
  const isLoadedRef = useRef(false);

  const normalizedCandidates = useMemo(() => {
    const canonicalList: ImageCandidate[] = [];
    if (src) {
      canonicalList.push({ src });
    }
    if (candidates?.length) {
      canonicalList.push(...candidates);
    }

    const uniqueByUrl = new Map<string, ImageCandidate>();
    canonicalList
      .filter((candidate) => Boolean(candidate.src))
      .forEach((candidate) => {
        const normalized = normalizeCandidate(candidate);
        uniqueByUrl.set(normalized.src, normalized);
      });

    return Array.from(uniqueByUrl.values());
  }, [src, candidates]);

  const fallback = useMemo(() => optimizeImageUrl(fallbackSrc), [fallbackSrc]);
  const canonicalCandidate = normalizedCandidates[0];
  const targetImage = canonicalCandidate?.src || fallback;
  const objectFitClass = fit === 'contain' ? 'object-contain' : 'object-cover';

  const srcSet = useMemo(() => {
    if (isBlobSource) {
      return '';
    }

    const responsiveCandidates = normalizedCandidates.filter(
      (candidate) => candidate.width && candidate.src !== resolvedSrcRef.current
    );

    if (!responsiveCandidates.length) {
      return '';
    }

    return responsiveCandidates.map((candidate) => `${candidate.src} ${candidate.width}w`).join(', ');
  }, [normalizedCandidates, isBlobSource]);

  const widthAttr = canonicalCandidate?.width;
  const heightAttr = canonicalCandidate?.height;

  const canonicalOrigin = useMemo(() => {
    try {
      return new URL(targetImage).origin;
    } catch {
      return null;
    }
  }, [targetImage]);

  // Maintain window intersection listener for lazy loading
  useEffect(() => {
    if (process.env.NODE_ENV === 'test' || priority) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  useEffect(() => {
    resolvedSrcRef.current = targetImage;
    setResolvedSrc(targetImage);
  }, [targetImage]);

  const [resolvedSrc, setResolvedSrc] = useState<string>(targetImage);

  useEffect(() => {
    isLoadedRef.current = isLoaded;
  }, [isLoaded]);

  useEffect(() => {
    let isCancelled = false;
    const shouldBypassCache = priority;

    const applyResolvedSource = (finalSrc: string, cacheHit: boolean) => {
      if (isCancelled) {
        return;
      }

      cacheHitRef.current = cacheHit;
      setHasError(false);
      setIsBlobSource(finalSrc.startsWith('blob:'));

      if (finalSrc !== resolvedSrcRef.current) {
        resolvedSrcRef.current = finalSrc;
        setResolvedSrc(finalSrc);
        setIsLoaded(false);
        loadStartRef.current = Date.now();
      } else if (isLoadedRef.current) {
        setIsLoaded(true);
      } else {
        loadStartRef.current = Date.now();
      }
    };

    const resolveSource = async () => {
      if (!inView) {
        applyResolvedSource(targetImage, false);
        return;
      }

      if (shouldBypassCache || !canonicalCandidate?.src || !isImageCacheAvailable()) {
        if (objectUrlRef.current) {
          releaseObjectUrl(objectUrlRef.current);
          objectUrlRef.current = null;
        }
        setIsBlobSource(false);
        applyResolvedSource(targetImage, false);
        return;
      }

      try {
        const { url, cacheHit, objectUrl } = await getCachedImage(canonicalCandidate.src);

        if (isCancelled) {
          if (objectUrl) {
            releaseObjectUrl(objectUrl);
          }
          return;
        }

        if (objectUrlRef.current && objectUrlRef.current !== objectUrl) {
          releaseObjectUrl(objectUrlRef.current);
        }

        objectUrlRef.current = objectUrl ?? null;
        applyResolvedSource(url, cacheHit);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          debugWarn('Content image cache resolution failed, using fallback source', error);
        }

        if (objectUrlRef.current) {
          releaseObjectUrl(objectUrlRef.current);
          objectUrlRef.current = null;
        }

        setIsBlobSource(false);
        applyResolvedSource(targetImage, false);
      }
    };

    resolveSource();

    return () => {
      isCancelled = true;
    };
  }, [canonicalCandidate, targetImage, inView, priority]);

  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        releaseObjectUrl(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    },
    []
  );

  const handleImageLoad = () => {
    const loadTime = Date.now() - loadStartRef.current;
    setIsLoaded(true);

    if (onLoadComplete) {
      onLoadComplete(loadTime);
    }

    if (process.env.NODE_ENV === 'development') {
      debugLog(`Content image loaded in ${loadTime}ms:`, resolvedSrc || targetImage);
    }
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!hasError) {
      const target = e.target as HTMLImageElement;
      target.src = fallback;
      setHasError(true);
      setIsLoaded(true);
      cacheHitRef.current = false;
      setIsBlobSource(false);

      if (objectUrlRef.current) {
        releaseObjectUrl(objectUrlRef.current);
        objectUrlRef.current = null;
      }

      setResolvedSrc(fallback);

      if (process.env.NODE_ENV === 'development') {
        debugWarn('Content image failed to load, using fallback:', canonicalCandidate?.src);
      }
    }
  };

  const shouldRenderSrcSet = !isBlobSource && Boolean(srcSet);

  return (
    <>
      {(preconnect || preload) && targetImage && (
        <Helmet>
          {preconnect && canonicalOrigin && (
            <link rel="preconnect" href={canonicalOrigin} crossOrigin="anonymous" />
          )}
          {preload && (
            <link
              rel="preload"
              as="image"
              href={targetImage}
              {...(shouldRenderSrcSet ? { imagesrcset: srcSet, imagesizes: sizes } : {})}
            />
          )}
        </Helmet>
      )}
      <div ref={containerRef} className={`relative ${className}`}>
        {!isLoaded && !hasError && (
          <div className="absolute inset-0 z-10">
            <LoadingSkeleton height="h-full" width="w-full" />
          </div>
        )}

        {inView && (
          <img
            src={resolvedSrc || fallback}
            alt={alt}
            loading={priority ? 'eager' : 'lazy'}
            {...(priority && { fetchpriority: 'high' })}
            decoding="async"
            className={`w-full h-full ${objectFitClass} transition-opacity duration-300 ${imageClassName}`}
            style={{
              opacity: isLoaded ? 1 : 0,
              ...imageStyle
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            width={widthAttr}
            height={heightAttr}
            {...(shouldRenderSrcSet ? { srcSet, sizes } : { srcSet: undefined, sizes: undefined })}
          />
        )}
      </div>
    </>
  );
};

export default OptimizedContentImage;
